/**
 * Polling utility for Word export status.
 * 
 * This polls the export status endpoint until completion, failure, or timeout.
 * Used as a fallback when realtime notifications are not available.
 * 
 * @param exportId - The export ID to poll for
 * @param options - Configuration options including callbacks and timing
 * @returns Cleanup function to stop polling
 */

interface ExportStatus {
  id: number
  status: string
  display_name: string | null
  download_url: string | null
  created_at: string
  updated_at: string
}

interface PollOptions {
  onCompleted?: (data: ExportStatus) => void
  onFailed?: (error: Error) => void
  onTimeout?: () => void
  maxDuration?: number // milliseconds
  interval?: number // milliseconds
}

const API_URL = (import.meta.env.VITE_API_URL as string) + '/api/v1/'

async function fetchExportStatus(exportId: number): Promise<ExportStatus> {
  const auth = localStorage.getItem('auth')
  if (!auth) {
    throw new Error('No auth token found')
  }

  const { token } = JSON.parse(auth)
  if (!token) {
    throw new Error('No auth token found')
  }

  const response = await fetch(`${API_URL}report-exports/${exportId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Export not found')
    }
    const error = new Error(`Failed to fetch export status: ${response.status}`) as any
    error.status = response.status
    throw error
  }

  const data = await response.json()
  return data.data
}

function downloadFile(url: string) {
  if (!url) return
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', '')
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export function pollForExportCompletion(
  exportId: number,
  options: PollOptions = {}
): () => void {
  const {
    onCompleted,
    onFailed,
    onTimeout,
    maxDuration = 5 * 60 * 1000, // 5 minutes default
    interval = 3000, // 3 seconds default
  } = options

  const startTime = Date.now()
  const MAX_ATTEMPTS = Math.floor(maxDuration / interval)
  let attempts = 0
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let isStopped = false

  const poll = async () => {
    if (isStopped) return
    attempts++

    // Stop if max duration exceeded
    if (Date.now() - startTime > maxDuration) {
      isStopped = true
      onTimeout?.()
      return
    }

    // Stop if max attempts exceeded
    if (attempts >= MAX_ATTEMPTS) {
      isStopped = true
      onTimeout?.()
      return
    }

    try {
      const status = await fetchExportStatus(exportId)

      if (isStopped) return

      if (status.status === 'completed') {
        isStopped = true
        if (status.download_url) {
          downloadFile(status.download_url)
        }
        onCompleted?.(status)
      } else if (status.status === 'failed') {
        isStopped = true
        onFailed?.(new Error('Export generation failed on server.'))
      } else {
        // Continue polling...
        timeoutId = setTimeout(poll, interval)
      }
    } catch (error: any) {
      if (isStopped) return
      // 404 means export likely deleted or DB lag; stop polling
      if (error instanceof Error && error.message === 'Export not found') {
        isStopped = true
        onFailed?.(new Error('Export not found.'))
        return
      }
      // Stop polling on auth revocation
      if (error?.status === 401 || error?.status === 403) {
        isStopped = true
        onFailed?.(new Error(`Authentication failed: ${error.status}`))
        return
      }
      // Ignore transient network errors, keep polling
      console.error('Error polling export status:', error)
      timeoutId = setTimeout(poll, interval)
    }
  }

  // Start polling
  poll()

  // Return cleanup function
  return () => {
    isStopped = true
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }
}

